/**
 * NEXUMA — Rolet & Privilegjet (RBAC)
 *
 * Ridizajn vizual + UX (Nexuma teal, enterprise).
 * Logjika e privilegjeve, API-të, payload-et, dhe permission keys MBETEN të paprekur.
 *
 * Funksionalitet:
 *  • 3-zone layout: header sticky · sidebar 300px · main workspace
 *  • Draft state për rol (add/remove sets) — Ruaj / Anulo aktivohen vetëm me ndryshime
 *  • Matrica si Akorion sipas modulit · veprime si rreshta · shtrirja si segmented chips
 *  • Status pill live (● X aktive) që ndryshon ngjyrë në draft (amber)
 *  • Review Drawer 480px me diff të grupuar sipas modulit
 *  • Critical confirm vetëm për dhënien e Fshij / Menaxho / shtrirjes "Të gjitha"
 *  • Auto-collapse të modulit kur "Vetëm aktive" është aktiv dhe ai modul ka 0 privilegje
 *  • Tab "Përdoruesit" ruan UserOverridesPanel ekzistuese
 *
 * TODO (backend): kur të ekzistojë endpoint-i `PATCH /api/roles/:roleKey/permissions`,
 * lidhe brenda funksionit `persistDraft`.
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Drawer, FormControlLabel, IconButton, InputAdornment,
  Paper, Snackbar, Stack, Switch, Tab, Tabs, TextField, Tooltip,
  Typography, alpha,
} from '@mui/material';
import SearchIcon                       from '@mui/icons-material/Search';
import CloseRoundedIcon                 from '@mui/icons-material/CloseRounded';
import AddIcon                          from '@mui/icons-material/Add';
import SaveRoundedIcon                  from '@mui/icons-material/SaveRounded';
import VisibilityOutlinedIcon           from '@mui/icons-material/VisibilityOutlined';
import AddCircleOutlineRoundedIcon      from '@mui/icons-material/AddCircleOutlineRounded';
import EditOutlinedIcon                 from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon         from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleOutlineRoundedIcon    from '@mui/icons-material/CheckCircleOutlineRounded';
import CancelOutlinedIcon               from '@mui/icons-material/CancelOutlined';
import FileDownloadOutlinedIcon         from '@mui/icons-material/FileDownloadOutlined';
import TuneRoundedIcon                  from '@mui/icons-material/TuneRounded';
import AssignmentIndOutlinedIcon        from '@mui/icons-material/AssignmentIndOutlined';
import LockOutlinedIcon                 from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon             from '@mui/icons-material/LockOpenOutlined';
import ExpandMoreRoundedIcon            from '@mui/icons-material/ExpandMoreRounded';
import WarningAmberRoundedIcon          from '@mui/icons-material/WarningAmberRounded';
import HistoryRoundedIcon               from '@mui/icons-material/HistoryRounded';
import RestartAltRoundedIcon            from '@mui/icons-material/RestartAltRounded';
import DoneAllRoundedIcon               from '@mui/icons-material/DoneAllRounded';
import UnfoldMoreRoundedIcon            from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRoundedIcon            from '@mui/icons-material/UnfoldLessRounded';
import PersonIcon                       from '@mui/icons-material/Person';
import SecurityRoundedIcon              from '@mui/icons-material/SecurityRounded';
import ContentCopyRoundedIcon           from '@mui/icons-material/ContentCopyRounded';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import LockRoundedIcon                  from '@mui/icons-material/LockRounded';

import usePermissions     from '../hooks/usePermissions';
import AccessDenied       from '../components/rbac/AccessDenied';
import { ROLES_CONFIG, ROLES }                                          from '../config/permissions/rolesConfig';
import { MODULES, PERMISSIONS, p }                                        from '../config/permissions/permissionsConfig';
import * as rolesService                                                 from '../services/rolePermissionsService';

/* ═════════════════════════════════════════════════════════════════════════════
 * Tokens
 * ═════════════════════════════════════════════════════════════════════════════ */
const T = {
  brand: '#3D8FA3', brandHover: '#327C8E',
  brandLight: '#E7F4F7', brandSoft: '#F2FAFC',
  // Errësuar një hap secili që teksti sekondar/i heshtur të mos dalë i zbehtë (si rregullimi global i temës).
  ink: '#0F172A', ink700: '#334155', ink500: '#475569',
  ink400: '#64748B', ink300: '#94A3B8',
  line: '#E2E8F0', lineSoft: '#EEF2F6',
  surface: '#F8FAFC', surfaceAlt: '#F1F5F9', white: '#FFFFFF',
  success: '#10B981', successBg: '#ECFDF5', successInk: '#059669',
  warning: '#F59E0B', warningBg: '#FEF3C7', warningInk: '#92400E',
  error:   '#EF4444', errorBg:   '#FEF2F2', errorInk:   '#DC2626',
};

const cardShell = {
  bgcolor: T.white,
  borderRadius: '14px',
  border: `1px solid ${T.line}`,
  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
};

/* ═════════════════════════════════════════════════════════════════════════════
 * Etiketat e moduleve (mbeten të njëjta)
 * ═════════════════════════════════════════════════════════════════════════════ */
const MODULE_LABELS = {
  [MODULES.DASHBOARD]:         'Paneli',
  [MODULES.EMPLOYEES]:         'Punonjësit',
  [MODULES.CONTRACTS]:         'Kontratat',
  [MODULES.ATTENDANCE]:        'Prezencat',
  [MODULES.LEAVE]:             'Pushimet',
  [MODULES.PAYROLL]:           'Pagat',
  [MODULES.RECRUITMENT]:       'Rekrutimi',
  [MODULES.PERFORMANCE]:       'Performanca',
  [MODULES.DOCUMENTS]:         'Dokumentet',
  [MODULES.REPORTS]:           'Raportet',
  [MODULES.SETTINGS]:          'Cilësimet',
  [MODULES.AUDIT_LOGS]:        'Auditimi',
  [MODULES.ROLES_PERMISSIONS]: 'Rolet & Privilegjet',
  [MODULES.PROJECTS]:          'Projektet',
  [MODULES.WARNINGS]:          'Vërejtjet',
  [MODULES.COMPLIANCE]:        'Përputhshmëria',
  [MODULES.COMMUNICATION]:     'Komunikimi',
  [MODULES.LEAVE_ADJUSTMENTS]: 'Rregullimet e pushimeve',
  [MODULES.ASSETS]:            'Asetet',
};

/** Emri i modulit; një modul i ri pa etiketë tregohet me çelësin e vet, jo bosh. */
const moduleLabel = (key) => MODULE_LABELS[key]
  || String(key || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * Katalogu i privilegjeve — i NXJERRË nga `PERMISSIONS`, jo i shkruar me dorë.
 *
 * Lista këtu ishte e shtypur rresht për rresht, ndaj mbetej pas: modulet
 * «Asetet», «Komunikimi» dhe «Rregullimet e pushimeve» kishin leje te sistemi,
 * por nuk dilnin askund te ndërtuesi i roleve — pra një rol i ri s'mund t'i
 * jepte kurrë ato, sado «i personalizuar» të quhej.
 *
 * Tani çdo leje e njohur nga aplikacioni shfaqet vetvetiu. Rendi i veprimeve
 * dhe i fushëveprimeve mbetet i qëndrueshëm, që ekrani të mos rirenditet vetë
 * sa herë shtohet një leje e re.
 */
const CATALOG_ACTION_ORDER = ['view', 'create', 'edit', 'delete', 'approve', 'reject', 'assign', 'export', 'manage', 'lock', 'unlock'];
const CATALOG_SCOPE_ORDER  = ['own', 'team', 'department', 'company', 'all'];
const rank = (list, v) => { const i = list.indexOf(v); return i < 0 ? list.length : i; };

const MODULE_PERMISSIONS_MAP = (() => {
  const out = {};
  for (const perm of new Set(Object.values(PERMISSIONS))) {
    if (typeof perm !== 'string') continue;
    const [module, action, scope] = perm.split('.');
    if (!module || !action) continue;
    (out[module] = out[module] || []).push({ action, ...(scope ? { scope } : {}) });
  }
  for (const list of Object.values(out)) {
    list.sort((x, y) => rank(CATALOG_ACTION_ORDER, x.action) - rank(CATALOG_ACTION_ORDER, y.action)
      || rank(CATALOG_SCOPE_ORDER, x.scope) - rank(CATALOG_SCOPE_ORDER, y.scope));
  }
  return out;
})();


/* ═════════════════════════════════════════════════════════════════════════════
 * Veprimet & Shtrirjet
 * ═════════════════════════════════════════════════════════════════════════════ */
const ACTION_DEFS = {
  view:    { label: 'Shiko',    icon: VisibilityOutlinedIcon,        order: 1 },
  create:  { label: 'Krijo',    icon: AddCircleOutlineRoundedIcon,   order: 2 },
  edit:    { label: 'Ndrysho',  icon: EditOutlinedIcon,              order: 3 },
  approve: { label: 'Aprovo',   icon: CheckCircleOutlineRoundedIcon, order: 4 },
  reject:  { label: 'Refuzo',   icon: CancelOutlinedIcon,            order: 5 },
  assign:  { label: 'Cakto',    icon: AssignmentIndOutlinedIcon,     order: 6 },
  export:  { label: 'Eksporto', icon: FileDownloadOutlinedIcon,      order: 7 },
  lock:    { label: 'Kyç',      icon: LockOutlinedIcon,              order: 8 },
  unlock:  { label: 'Çkyç',     icon: LockOpenOutlinedIcon,          order: 9 },
  delete:  { label: 'Fshij',    icon: DeleteOutlineRoundedIcon,      order: 10, critical: true },
  manage:  { label: 'Menaxho',  icon: TuneRoundedIcon,               order: 11, critical: true },
};

const SCOPE_LABELS = {
  own: 'Veta', team: 'Ekipi', department: 'Departamenti', company: 'Kompania', all: 'Të gjitha',
};
const SCOPE_ORDER = ['own', 'team', 'department', 'company', 'all'];

const EMPTY_DRAFT = { add: new Set(), remove: new Set() };

/* ═════════════════════════════════════════════════════════════════════════════
 * Pamja e thjeshtë — tri kolona
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Matrica e plotë ka njëmbëdhjetë veprime dhe pesë shtrirje për modul. Kush e
 * cakton një rol një herë në gjashtë muaj nuk pyet «a e sheh ekipin apo
 * departamentin» — pyet a mund ta shohë modulin, a mund ta ndryshojë, a mund
 * ta fshijë. Prandaj kjo është pamja e parazgjedhur: tri kolona për modul.
 *
 * Asgjë nuk hiqet. Çelësi «Të avancuara» kthen matricën me shtrirje, dhe të dy
 * pamjet shkruajnë te i njëjti grumbull lejesh — një kuti e shënuar këtu është
 * po ajo leje që atje duket me shtrirjen e vet.
 */
const SIMPLE_COLUMNS = [
  // `short` për telefonin: «Lejo shikimin» × tri kolona e shtynte tabelën
  // dyfishin e gjerësisë së ekranit dhe e detyronte rrëshqitjen anash.
  { key: 'view',   label: 'Lejo shikimin', short: 'Shiko', actions: ['view'] },
  { key: 'edit',   label: 'Lejo editimin', short: 'Edito', actions: ['create', 'edit'] },
  { key: 'delete', label: 'Lejo fshirjen', short: 'Fshi',  actions: ['delete'], critical: true },
];

/**
 * Sa larg shtrihet një kuti e shënuar këtu.
 *
 * «Kompania» e para: kush e jep të drejtën nga kjo pamje e ka fjalën për tërë
 * kompaninë, jo për një ekip. Kur moduli s'e njeh atë shtrirje, merret më e
 * gjera që ekziston. `all` mbetet e fundit — është ndërtenant dhe s'jepet pa
 * u kërkuar shprehimisht te pamja e avancuar.
 */
const SIMPLE_GRANT_SCOPES = ['company', 'department', 'team', 'own'];

/** Çdo çelës leje i këtyre veprimeve te ky modul — të gjitha shtrirjet. */
function simpleKeys(moduleKey, actions) {
  return (MODULE_PERMISSIONS_MAP[moduleKey] || [])
    .filter(({ action }) => actions.includes(action))
    .map(({ action, scope }) => cellKey(moduleKey, { action, scope }));
}

/** Një çelës për veprim — ai që do të jepet kur shënohet kutia. */
function simpleGrantKeys(moduleKey, actions) {
  const perms = MODULE_PERMISSIONS_MAP[moduleKey] || [];
  const out = [];
  for (const action of actions) {
    const forAction = perms.filter(x => x.action === action);
    if (!forAction.length) continue;
    const noScope = forAction.find(x => !x.scope);
    const picked = noScope
      || SIMPLE_GRANT_SCOPES.map(sc => forAction.find(x => x.scope === sc)).find(Boolean)
      || forAction[0];
    out.push(cellKey(moduleKey, picked));
  }
  return out;
}

/**
 * Veprimet që nuk hyjnë në tri kolonat.
 *
 * Tri kolonat mbulojnë katër nga njëmbëdhjetë veprime. `approve`, `reject`,
 * `assign`, `export`, `lock`, `unlock` dhe sidomos `manage` mbeteshin krejt të
 * padukshme: një rol mund të kishte `payroll.manage` — kontroll të plotë mbi
 * pagat — dhe tabela s'tregonte asgjë. Prandaj rreshti mban edhe një kolonë të
 * gjashtë që i numëron dhe i emërton, me një hyrje te pamja e avancuar.
 */
const SIMPLE_COVERED_ACTIONS = SIMPLE_COLUMNS.flatMap(c => c.actions);

function simpleExtras(moduleKey, effective) {
  const seen = new Set();
  const out = [];
  for (const { action } of MODULE_PERMISSIONS_MAP[moduleKey] || []) {
    if (SIMPLE_COVERED_ACTIONS.includes(action) || seen.has(action)) continue;
    seen.add(action);
    out.push({
      action,
      label: ACTION_DEFS[action]?.label || action,
      critical: Boolean(ACTION_DEFS[action]?.critical),
      granted: simpleKeys(moduleKey, [action]).some(k => effective.has(k)),
    });
  }
  return out.sort((a, b) => (ACTION_DEFS[a.action]?.order || 99) - (ACTION_DEFS[b.action]?.order || 99));
}

/**
 * Çfarë jep saktësisht një kuti — teksti i tooltip-it.
 *
 * «Lejo editimin» mund të thotë Krijo te një modul dhe Krijo + Ndrysho te
 * tjetri, me shtrirje të ndryshme. Pa këtë, kutia është një premtim i pathënë.
 */
function simpleCellDetail(moduleKey, col, effective) {
  const present = simpleActionsPresent(moduleKey, col.actions);
  if (!present.length) return 'Ky modul nuk e ka këtë veprim';
  return present.map(action => {
    const granted = (MODULE_PERMISSIONS_MAP[moduleKey] || [])
      .filter(x => x.action === action && effective.has(cellKey(moduleKey, x)))
      .map(x => (x.scope ? SCOPE_LABELS[x.scope] : 'pa shtrirje'));
    const name = ACTION_DEFS[action]?.label || action;
    return granted.length ? `${name}: ${granted.join(', ')}` : `${name}: jo`;
  }).join(' · ');
}

/** Cilat nga veprimet e kolonës i njeh fare ky modul. */
function simpleActionsPresent(moduleKey, actions) {
  const perms = MODULE_PERMISSIONS_MAP[moduleKey] || [];
  return actions.filter(a => perms.some(x => x.action === a));
}

/**
 * Gjendja e një kutie: 'off' | 'partial' | 'on' | null (moduli s'e ka).
 *
 * «partial» ekziston sepse një kuti mbulon disa veprime (Edito = Krijo +
 * Ndrysho): pa të, një rol që krijon por nuk ndryshon do të dukej sikur i ka
 * të dyja, ose asnjërën.
 */
function simpleCellState(moduleKey, actions, effective) {
  const present = simpleActionsPresent(moduleKey, actions);
  if (!present.length) return null;
  const granted = present.filter(a => simpleKeys(moduleKey, [a]).some(k => effective.has(k)));
  if (granted.length === 0) return 'off';
  return granted.length === present.length ? 'on' : 'partial';
}



/* ═════════════════════════════════════════════════════════════════════════════
 * Helpers
 * ═════════════════════════════════════════════════════════════════════════════ */
function cellKey(module, { action, scope }) {
  return p(module, action, scope || null);
}

function buildModuleActions(moduleKey) {
  const perms = MODULE_PERMISSIONS_MAP[moduleKey] || [];
  const byAction = {};
  perms.forEach(({ action, scope }) => {
    if (!byAction[action]) byAction[action] = { action, scopes: [], hasNoScope: false };
    if (scope) byAction[action].scopes.push(scope);
    else byAction[action].hasNoScope = true;
  });
  Object.values(byAction).forEach(a => {
    a.scopes.sort((x, y) => SCOPE_ORDER.indexOf(x) - SCOPE_ORDER.indexOf(y));
  });
  return Object.values(byAction).sort((a, b) =>
    (ACTION_DEFS[a.action]?.order || 99) - (ACTION_DEFS[b.action]?.order || 99)
  );
}

function effectiveSetFor(baseSet, draft) {
  const out = new Set(baseSet);
  draft.add.forEach(p => out.add(p));
  draft.remove.forEach(p => out.delete(p));
  return out;
}

function computeNextDraft(permKey, baseSet, draft, turnOn) {
  const isBase = baseSet.has(permKey);
  const next = { add: new Set(draft.add), remove: new Set(draft.remove) };
  if (turnOn) {
    if (isBase) next.remove.delete(permKey);
    else        { next.add.add(permKey); next.remove.delete(permKey); }
  } else {
    if (isBase) { next.remove.add(permKey); next.add.delete(permKey); }
    else        next.add.delete(permKey);
  }
  return next;
}

function isCriticalToggle({ action, scope }) {
  if (ACTION_DEFS[action]?.critical) return true;
  if (scope === 'all') return true;
  return false;
}

function isSystemRole(roleKey) {
  return ['super_admin', 'auditor'].includes(roleKey);
}

function roleGroup(role) {
  if (['super_admin', 'company_admin'].includes(role.key)) return 'sistemi';
  if (['auditor'].includes(role.key)) return 'mjedis';
  return 'funksionale';
}
const GROUP_LABELS = {
  sistemi:       'SISTEMI',
  funksionale:   'ROLE FUNKSIONALE',
  mjedis:        'MJEDIS',
  custom:        'ROLE TË KRIJUARA',
};

/* ═════════════════════════════════════════════════════════════════════════════
 * Atoms
 * ═════════════════════════════════════════════════════════════════════════════ */
function PriorityBadge({ priority }) {
  let tone = { bg: T.surfaceAlt, fg: T.ink500 };
  if (priority >= 90)       tone = { bg: T.brand,      fg: T.white };
  else if (priority >= 70)  tone = { bg: T.brandLight, fg: T.brand };
  else if (priority >= 30)  tone = { bg: T.surfaceAlt, fg: T.ink700 };
  else                      tone = { bg: T.warningBg,  fg: T.warningInk };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 36, height: 20, px: 0.75, borderRadius: '6px',
      bgcolor: tone.bg, color: tone.fg,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 11.5, fontWeight: 700, letterSpacing: 0.2,
    }}>P{priority}</Box>
  );
}

function StatusPill({ count, dirty }) {
  const color = dirty ? T.warning : T.success;
  const bg    = dirty ? T.warningBg : T.successBg;
  const fg    = dirty ? T.warningInk : T.successInk;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75,
      height: 28, px: 1.25, borderRadius: '999px',
      bgcolor: bg, color: fg,
      fontSize: 12.5, fontWeight: 600, lineHeight: 1,
      whiteSpace: 'nowrap',
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
      {count} aktive
      {dirty && (
        <Box sx={{
          ml: 0.5, height: 16, px: 0.625, borderRadius: '999px',
          bgcolor: alpha(T.warning, 0.25),
          fontSize: 10.5, fontWeight: 700, color: T.warningInk,
          display: 'inline-flex', alignItems: 'center', letterSpacing: 0.3,
        }}>DRAFT</Box>
      )}
    </Box>
  );
}

function SectionLabel({ children, count, action, sx }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={sx}>
      <Typography sx={{
        fontSize: 11, fontWeight: 700, color: T.ink400,
        textTransform: 'uppercase', letterSpacing: 0.7,
      }}>{children}</Typography>
      {count != null && (
        <Box sx={{
          minWidth: 18, height: 16, px: 0.625, borderRadius: '999px',
          bgcolor: T.surfaceAlt, color: T.ink500,
          fontSize: 10, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{count}</Box>
      )}
      {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
    </Stack>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * RoleCard (sidebar)
 * ═════════════════════════════════════════════════════════════════════════════ */
function RoleCard({ role, selected, draftCount, readOnly, onClick }) {
  const pCount = role.permissions?.length || 0;
  return (
    <Box
      role="button"
      onClick={onClick}
      sx={{
        position: 'relative',
        px: 1.5, py: 1.25,
        display: 'flex', alignItems: 'center', gap: 1.25,
        cursor: 'pointer',
        borderLeft: '3px solid',
        borderLeftColor: selected ? T.brand : 'transparent',
        bgcolor: selected ? T.brandSoft : 'transparent',
        transition: 'all 140ms ease',
        '&:hover': { bgcolor: selected ? T.brandSoft : T.surface },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: T.ink, lineHeight: 1.3 }}>
            {role.label}
          </Typography>
          <PriorityBadge priority={role.priority} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.375 }}>
          <Typography sx={{ fontSize: 12, color: T.ink500 }}>
            {pCount} privilegje
          </Typography>
          {readOnly && (
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.25,
              fontSize: 10.5, color: T.ink400,
            }}>
              <LockRoundedIcon sx={{ fontSize: 11 }} />
              vetëm lexim
            </Box>
          )}
          {draftCount > 0 && (
            <Box sx={{
              ml: 'auto',
              display: 'inline-flex', alignItems: 'center', gap: 0.375,
              height: 18, px: 0.625, borderRadius: '999px',
              bgcolor: T.warningBg, color: T.warningInk,
              fontSize: 10.5, fontWeight: 700, letterSpacing: 0.2,
            }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: T.warning }} />
              Po redaktoni · {draftCount}
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * ScopeChip (segmented)
 * ═════════════════════════════════════════════════════════════════════════════ */
function ScopeChip({ scope, selected, critical, disabled, onClick }) {
  const label = SCOPE_LABELS[scope] || scope;
  const isCritical = scope === 'all';
  let bg = T.surfaceAlt, fg = T.ink500, border = 'transparent';
  if (selected && isCritical) { bg = T.warningBg;   fg = T.warningInk;  border = alpha(T.warning, 0.4); }
  else if (selected)          { bg = T.brandLight;  fg = T.brand;       border = alpha(T.brand, 0.35); }
  return (
    <Box
      role="button"
      onClick={disabled ? undefined : onClick}
      sx={{
        userSelect: 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        height: 28, px: 1.25, borderRadius: '8px',
        bgcolor: bg, color: fg,
        border: `1px solid ${border}`,
        fontSize: 12, fontWeight: selected ? 700 : 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'all 140ms ease',
        whiteSpace: 'nowrap',
        '&:hover': disabled ? {} : {
          bgcolor: selected ? bg : T.brandSoft,
          color:   selected ? fg : T.ink700,
        },
      }}
    >
      {isCritical && (
        <WarningAmberRoundedIcon sx={{ fontSize: 12, mr: 0.5, color: selected ? T.warningInk : T.warning }} />
      )}
      {label}
    </Box>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * ActionRow
 * ═════════════════════════════════════════════════════════════════════════════ */
/**
 * Tabela e thjeshtë: një rresht për modul, tri kutiza.
 *
 * Pa akordeone dhe pa pilula — moduli lexohet si rresht, dhe e tërë e drejta e
 * tij shihet pa e hapur. Me tridhjetë module kjo është një faqe që shfletohet,
 * jo tridhjetë blloqe që hapen një nga një.
 */
/**
 * Kolona «Të tjera»: veprimet që tri kutizat nuk i prekin.
 *
 * Nuk janë kutiza me qëllim — «Aprovo», «Kyç» dhe «Menaxho» nuk jepen me një
 * klikim pa e parë shtrirjen. Këtu vetëm thuhet çfarë ka roli, dhe kalohet te
 * pamja e avancuar për ta ndryshuar.
 */
function SimpleExtras({ moduleKey, extras, onOpen }) {
  if (!extras.length) {
    return <Typography sx={{ fontSize: 12.5, color: T.ink300, display: { xs: 'none', sm: 'block' } }}>—</Typography>;
  }
  const granted = extras.filter(e => e.granted);
  return (
    <Tooltip
      arrow placement="top"
      title={extras.map(e => `${e.label}: ${e.granted ? 'po' : 'jo'}`).join(' · ') + ' — kliko për t\'i ndryshuar'}
    >
      <Box
        role="button" onClick={onOpen}
        sx={{
          display: { xs: 'none', sm: 'flex' }, flexWrap: 'wrap', gap: 0.4,
          alignItems: 'center', cursor: 'pointer', borderRadius: '6px', px: 0.5, py: 0.25,
          '&:hover': { bgcolor: T.surfaceAlt },
        }}
      >
        {granted.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: T.ink400 }}>0 nga {extras.length}</Typography>
        ) : granted.map(e => (
          <Box key={e.action} sx={{
            fontSize: 11, fontWeight: 700, px: 0.6, py: 0.1, borderRadius: '999px',
            bgcolor: e.critical ? T.errorBg : T.brandLight,
            color: e.critical ? T.errorInk : T.brand,
          }}>
            {e.label}
          </Box>
        ))}
      </Box>
    </Tooltip>
  );
}

function SimpleMatrix({ modules, effective, disabled, onToggle, onOpenAdvanced }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: { xs: 0, sm: 640 } }}>
        {/* Koka */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '28px minmax(0, 1fr) repeat(3, 52px)', sm: '44px minmax(120px, 1fr) repeat(3, 96px) minmax(140px, 0.9fr)' },
          alignItems: 'center', gap: 1,
          px: { xs: 1, md: 2 }, py: 1.25,
          borderBottom: `2px solid ${T.line}`,
          position: 'sticky', top: 0, zIndex: 1, bgcolor: T.white,
        }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink500 }}>Nr</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink500 }}>Moduli</Typography>
          {SIMPLE_COLUMNS.map(col => (
            <Typography key={col.key} align="center" sx={{ fontSize: 12, fontWeight: 700, color: T.ink500 }}>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{col.label}</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{col.short}</Box>
            </Typography>
          ))}
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink500, display: { xs: 'none', sm: 'block' } }}>
            Të tjera
          </Typography>
        </Box>

        {modules.map((moduleKey, i) => (
          <Box key={moduleKey} sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '28px minmax(0, 1fr) repeat(3, 52px)', sm: '44px minmax(120px, 1fr) repeat(3, 96px) minmax(140px, 0.9fr)' },
            alignItems: 'center', gap: 1,
            px: { xs: 1, md: 2 }, py: 0.5,
            bgcolor: i % 2 ? T.surface : 'transparent',
            borderBottom: i === modules.length - 1 ? 0 : `1px solid ${T.lineSoft}`,
          }}>
            <Typography sx={{ fontSize: 13, color: T.ink500, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</Typography>
            {/* Emrat e gjatë («Përputhshmëria») preknin kutinë e parë te telefoni. */}
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: T.ink, pr: 1, overflowWrap: 'anywhere' }}>
              {moduleLabel(moduleKey)}
            </Typography>
            {SIMPLE_COLUMNS.map(col => {
              const state = simpleCellState(moduleKey, col.actions, effective);
              if (state === null) {
                // Moduli s'e njeh fare këtë veprim — një kuti e zbrazët do të
                // premtonte një të drejtë që s'ekziston.
                return (
                  <Tooltip key={col.key} title="Ky modul nuk e ka këtë veprim" placement="top" arrow>
                    <Typography align="center" sx={{ fontSize: 13, color: T.ink300 }}>—</Typography>
                  </Tooltip>
                );
              }
              return (
                <Tooltip key={col.key} title={simpleCellDetail(moduleKey, col, effective)} placement="top" arrow>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={state === 'on'}
                    indeterminate={state === 'partial'}
                    disabled={disabled}
                    onChange={(e) => onToggle(moduleKey, col, e.target.checked)}
                    inputProps={{ 'aria-label': `${col.label} — ${moduleLabel(moduleKey)}` }}
                    sx={{
                      p: 0.75, color: T.ink300,
                      '&.Mui-checked': { color: col.critical ? T.errorInk : T.brand },
                      '&.MuiCheckbox-indeterminate': { color: T.warningInk },
                    }}
                  />
                </Box>
                </Tooltip>
              );
            })}
            <SimpleExtras
              moduleKey={moduleKey}
              extras={simpleExtras(moduleKey, effective)}
              onOpen={() => onOpenAdvanced(moduleKey)}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ActionRow({ moduleKey, action, scopes, hasNoScope, effective, disabled, even, isLast, onToggle }) {
  const def = ACTION_DEFS[action] || { label: action, icon: VisibilityOutlinedIcon };
  const Icon = def.icon;
  const isActionCritical = !!def.critical;

  // Permission key for the "no-scope" variant
  const noScopeKey = hasNoScope ? cellKey(moduleKey, { action, scope: null }) : null;
  const isNoScopeOn = noScopeKey ? effective.has(noScopeKey) : false;

  return (
    <Box sx={{
      display: 'grid',
      // Në telefon rreshti bëhet dy rreshta: emri i veprimit sipër, shtrirjet
      // poshtë tij. Me `180px 1fr` te një ekran 375px, kolona e parë hante gati
      // gjysmën dhe pilulat e shtrirjes mbeteshin me ~100px — «Departamenti»
      // dhe «Të gjitha» priteshin nga buza e kartës dhe s'kishte si të lexoheshe.
      gridTemplateColumns: { xs: '1fr', md: '200px 1fr' },
      alignItems: { xs: 'start', md: 'center' },
      columnGap: 2, rowGap: { xs: 0.75, md: 1 },
      px: { xs: 1.25, md: 2 }, py: 1.25,
      bgcolor: even ? T.surface : 'transparent',
      borderBottom: isLast ? 0 : `1px solid ${T.lineSoft}`,
    }}>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '8px',
          bgcolor: isActionCritical ? T.errorBg : T.brandLight,
          color: isActionCritical ? T.errorInk : T.brand,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: T.ink, lineHeight: 1.3 }}>
            {def.label}
          </Typography>
          {isActionCritical && (
            <Tooltip title="Veprim kritik — kërkon konfirmim" arrow placement="top">
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.25,
                height: 18, px: 0.625, borderRadius: '999px',
                bgcolor: T.warningBg, color: T.warningInk,
                fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
              }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 10 }} /> kritik
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Box>
        {scopes.length > 0 ? (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {scopes.map(scope => {
              const key = cellKey(moduleKey, { action, scope });
              const sel = effective.has(key);
              return (
                <ScopeChip
                  key={scope}
                  scope={scope}
                  selected={sel}
                  disabled={disabled}
                  onClick={() => onToggle(key, { action, scope }, !sel)}
                />
              );
            })}
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Checkbox
              checked={isNoScopeOn}
              disabled={disabled}
              onChange={(e) => onToggle(noScopeKey, { action, scope: null }, e.target.checked)}
              size="small"
              sx={{
                p: 0.5,
                color: T.ink300,
                '&.Mui-checked': { color: T.brand },
              }}
            />
            <Typography sx={{ fontSize: 12, color: T.ink400 }}>
              (pa shtrirje)
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * ModuleAccordion
 * ═════════════════════════════════════════════════════════════════════════════ */
function ModuleAccordion({
  moduleKey, expanded, activeCount, totalCount,
  disabled, onToggleExpanded, onReset, onFillAll, children,
}) {
  const label = moduleLabel(moduleKey);
  const ratio = totalCount > 0 ? activeCount / totalCount : 0;
  const empty = activeCount === 0;
  const full  = activeCount === totalCount && totalCount > 0;

  return (
    <Box sx={{
      borderBottom: `1px solid ${T.lineSoft}`,
      '&:last-of-type': { borderBottom: 0 },
    }}>
      {/* Header */}
      <Box
        role="button"
        onClick={onToggleExpanded}
        sx={{
          position: expanded ? 'sticky' : 'static',
          top: 0, zIndex: 2,
          px: { xs: 1.25, md: 2 }, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 1.25,
          cursor: 'pointer',
          bgcolor: expanded ? T.white : T.white,
          borderBottom: expanded ? `1px solid ${T.line}` : 0,
          transition: 'all 160ms ease',
          '&:hover': { bgcolor: T.surface },
        }}
      >
        <ExpandMoreRoundedIcon sx={{
          fontSize: 18, color: T.ink500,
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: 'transform 160ms ease',
        }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink, flex: 1 }}>
          {label}
        </Typography>

        {/* Progress dots — dekor; te telefoni numri «5 / 12» e thotë të njëjtën
            gjë pa i hequr hapësirë emrit të modulit. */}
        <Box sx={{ display: { xs: 'none', sm: 'inline-flex' }, gap: 0.375, flexShrink: 0 }}>
          {Array.from({ length: Math.min(totalCount, 12) }).map((_, i) => {
            const filled = i < Math.round(ratio * Math.min(totalCount, 12));
            return (
              <Box key={i} sx={{
                width: 5, height: 5, borderRadius: '50%',
                bgcolor: filled ? T.brand : T.surfaceAlt,
              }} />
            );
          })}
        </Box>

        <Typography sx={{
          fontSize: 12, fontWeight: 600, color: empty ? T.ink400 : T.ink700,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          minWidth: 44, textAlign: 'right', flexShrink: 0,
        }}>
          {activeCount} / {totalCount}
        </Typography>
      </Box>

      {/* Body */}
      {expanded && (
        <Box sx={{ bgcolor: T.white }}>
          {children}
          {!disabled && (
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{
              px: 2, py: 1,
              borderTop: `1px solid ${T.lineSoft}`,
              bgcolor: T.surface,
            }}>
              <Button
                size="small"
                onClick={onReset}
                startIcon={<RestartAltRoundedIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 28, px: 1, borderRadius: '6px',
                  textTransform: 'none', fontSize: 12, color: T.ink500,
                  '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
                }}
              >Reset modulin</Button>
              <Button
                size="small"
                onClick={onFillAll}
                disabled={full}
                startIcon={<DoneAllRoundedIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 28, px: 1, borderRadius: '6px',
                  textTransform: 'none', fontSize: 12, color: T.brand,
                  '&:hover': { bgcolor: T.brandSoft },
                  '&.Mui-disabled': { color: T.ink300 },
                }}
              >Plotëso</Button>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * DraftBanner
 * ═════════════════════════════════════════════════════════════════════════════ */
function DraftBanner({ count, onReview, onDiscard }) {
  if (count === 0) return null;
  return (
    <Box sx={{
      mt: 1.5,
      px: 1.5, py: 1.25,
      borderRadius: '10px',
      bgcolor: T.warningBg,
      border: `1px solid ${alpha(T.warning, 0.35)}`,
      display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
    }}>
      <WarningAmberRoundedIcon sx={{ fontSize: 18, color: T.warningInk }} />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.warningInk, flex: 1, minWidth: 200 }}>
        Ke {count} {count === 1 ? 'ndryshim' : 'ndryshime'} pa u ruajtur
      </Typography>
      <Stack direction="row" spacing={0.75}>
        <Button
          size="small" onClick={onReview}
          startIcon={<HistoryRoundedIcon sx={{ fontSize: 14 }} />}
          sx={{
            height: 28, px: 1.25, borderRadius: '6px',
            textTransform: 'none', fontSize: 12, fontWeight: 600,
            color: T.warningInk, bgcolor: alpha(T.warning, 0.15),
            '&:hover': { bgcolor: alpha(T.warning, 0.25) },
          }}
        >Shiko detajet</Button>
        <Button
          size="small" onClick={onDiscard}
          sx={{
            height: 28, px: 1.25, borderRadius: '6px',
            textTransform: 'none', fontSize: 12, color: T.warningInk,
            '&:hover': { bgcolor: alpha(T.warning, 0.1) },
          }}
        >Anulo ndryshimet</Button>
      </Stack>
    </Box>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * Critical Confirm
 * ═════════════════════════════════════════════════════════════════════════════ */
function CriticalConfirm({ open, payload, onCancel, onConfirm }) {
  if (!payload) {
    return (
      <Dialog open={open} onClose={onCancel} PaperProps={{ sx: { borderRadius: '14px' } }} />
    );
  }
  const { roleLabel, actionLabel, scopeLabel, moduleLabel } = payload;
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{ sx: { borderRadius: '14px', maxWidth: 380 } }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          bgcolor: T.warningBg, color: T.warningInk,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
          Konfirmoni veprimin kritik
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: T.ink700, lineHeight: 1.55 }}>
          Po i jep rolit <strong>{roleLabel}</strong> të drejtën të <strong>{actionLabel}</strong>{' '}
          {scopeLabel ? <>mbi <strong>{scopeLabel}</strong></> : null} në modulin{' '}
          <strong>{moduleLabel}</strong>.
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: T.ink500, mt: 1.25 }}>
          Ky privilegj është i klasifikuar si kritik. Vazhdoni vetëm nëse jeni i sigurt.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          sx={{
            height: 34, px: 1.5, borderRadius: '8px',
            color: T.ink700, textTransform: 'none', fontWeight: 500, fontSize: 13,
            '&:hover': { bgcolor: T.surfaceAlt },
          }}
        >Anulo</Button>
        <Button
          onClick={onConfirm}
          disableElevation
          sx={{
            height: 34, px: 1.75, borderRadius: '8px',
            bgcolor: T.warningInk, color: T.white,
            textTransform: 'none', fontWeight: 600, fontSize: 13,
            '&:hover': { bgcolor: '#7a3a08' },
          }}
        >Po, vazhdo</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * Review Drawer
 * ═════════════════════════════════════════════════════════════════════════════ */
function ReviewDrawer({
  open, onClose, roleLabel, addList, removeList,
  onJumpTo, onDiscardAll, onSave, saving,
}) {
  const total = addList.length + removeList.length;

  // Group by module
  const groupByModule = (list) => {
    const map = {};
    list.forEach(item => {
      if (!map[item.moduleKey]) map[item.moduleKey] = [];
      map[item.moduleKey].push(item);
    });
    return map;
  };
  const addByModule    = groupByModule(addList);
  const removeByModule = groupByModule(removeList);
  const allModules = Array.from(new Set([
    ...Object.keys(addByModule),
    ...Object.keys(removeByModule),
  ]));

  return (
    <Drawer
      anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, bgcolor: T.surface } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{
          px: 2.5, py: 2, borderBottom: `1px solid ${T.line}`,
          bgcolor: T.white,
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
                Ndryshimet · {total}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: T.ink500, mt: 0.25 }}>
                për rolin {roleLabel}
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{
              width: 32, height: 32, borderRadius: '8px', color: T.ink500,
              '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
            }}>
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, p: 2 }}>
          {total === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: 13, color: T.ink500 }}>Asnjë ndryshim.</Typography>
            </Box>
          ) : (
            allModules.map(moduleKey => (
              <Box key={moduleKey} sx={{ mb: 2 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 700, color: T.ink400,
                  textTransform: 'uppercase', letterSpacing: 0.6, mb: 1,
                }}>
                  {moduleLabel(moduleKey)}
                </Typography>
                <Stack spacing={0.625}>
                  {(addByModule[moduleKey] || []).map(item => (
                    <ReviewItem
                      key={'+' + item.permKey} kind="add"
                      label={item.label}
                      onClick={() => onJumpTo(item.moduleKey)}
                    />
                  ))}
                  {(removeByModule[moduleKey] || []).map(item => (
                    <ReviewItem
                      key={'-' + item.permKey} kind="remove"
                      label={item.label}
                      onClick={() => onJumpTo(item.moduleKey)}
                    />
                  ))}
                </Stack>
              </Box>
            ))
          )}
        </Box>

        {/* Footer */}
        <Box sx={{
          px: 2.5, py: 1.5, borderTop: `1px solid ${T.line}`, bgcolor: T.white,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
        }}>
          <Button
            onClick={onDiscardAll}
            disabled={total === 0 || saving}
            sx={{
              height: 34, px: 1.5, borderRadius: '8px',
              color: T.errorInk, textTransform: 'none', fontSize: 13, fontWeight: 500,
              '&:hover': { bgcolor: T.errorBg },
              '&.Mui-disabled': { color: T.ink300 },
            }}
          >Anulo gjithçka</Button>
          <Button
            onClick={onSave}
            disabled={total === 0 || saving}
            disableElevation
            startIcon={saving
              ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
              : <SaveRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              height: 34, px: 1.75, borderRadius: '8px',
              bgcolor: T.brand, color: T.white,
              textTransform: 'none', fontSize: 13, fontWeight: 600,
              '&:hover': { bgcolor: T.brandHover },
              '&.Mui-disabled': { bgcolor: T.surfaceAlt, color: T.ink400 },
            }}
          >{saving ? 'Duke ruajtur…' : `Ruaj ${total} ndryshime`}</Button>
        </Box>
      </Box>
    </Drawer>
  );
}

function ReviewItem({ kind, label, onClick }) {
  const isAdd = kind === 'add';
  return (
    <Box
      onClick={onClick}
      role="button"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.25, py: 0.875, borderRadius: '8px',
        bgcolor: T.white,
        border: `1px solid ${T.line}`,
        cursor: 'pointer',
        transition: 'all 140ms ease',
        '&:hover': {
          bgcolor: isAdd ? T.successBg : T.errorBg,
          borderColor: isAdd ? alpha(T.success, 0.35) : alpha(T.error, 0.35),
        },
      }}
    >
      <Box sx={{
        width: 20, height: 20, borderRadius: '6px',
        bgcolor: isAdd ? T.successBg : T.errorBg,
        color: isAdd ? T.successInk : T.errorInk,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, flexShrink: 0,
      }}>{isAdd ? '+' : '−'}</Box>
      <Typography sx={{ fontSize: 12.5, color: T.ink700, flex: 1, lineHeight: 1.4 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 16, color: T.ink400, lineHeight: 1 }}>›</Typography>
    </Box>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * RoleSummaryCard
 * ═════════════════════════════════════════════════════════════════════════════ */
function RoleSummaryCard({ role, draftCount, activeCount, readOnly, onReview, onDiscard, onClone, onRename }) {
  const moduleCount = useMemo(() => {
    const set = new Set();
    (role?.permissions || []).forEach(perm => {
      const mod = (perm || '').split('.')[0];
      if (mod) set.add(mod);
    });
    return set.size;
  }, [role]);

  return (
    <Paper elevation={0} sx={{ ...cardShell, p: 2.25 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: '12px',
          bgcolor: role.bgColor || T.brandLight,
          color: role.color || T.brand,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 700,
        }}>
          {role.label.charAt(0).toUpperCase()}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>
              {role.label}
            </Typography>
            <PriorityBadge priority={role.priority} />
            {readOnly && (
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 0.875, py: 0.25, borderRadius: '6px',
                bgcolor: T.surfaceAlt, color: T.ink500,
                fontSize: 11, fontWeight: 600,
              }}>
                <LockRoundedIcon sx={{ fontSize: 12 }} /> Vetëm lexim
              </Box>
            )}
          </Stack>
          {role.description && (
            <Typography sx={{ fontSize: 13, color: T.ink500, mt: 0.625, lineHeight: 1.5 }}>
              {role.description}
            </Typography>
          )}
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 12.5, color: T.ink500 }}>
              <Box component="span" sx={{ color: T.ink, fontWeight: 600 }}>{activeCount}</Box> privilegje
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: T.ink500 }}>
              <Box component="span" sx={{ color: T.ink, fontWeight: 600 }}>{moduleCount}</Box> module
            </Typography>
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.75}>
          <Button
            size="small"
            onClick={onClone}
            startIcon={<ContentCopyRoundedIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 32, px: 1.25, borderRadius: '8px',
              color: T.ink700, bgcolor: T.white,
              border: `1px solid ${T.line}`,
              textTransform: 'none', fontSize: 12.5, fontWeight: 500,
              '&:hover': { bgcolor: T.surface, borderColor: T.ink300 },
            }}
          >Klono</Button>
          <Button
            size="small"
            onClick={onRename}
            disabled={readOnly}
            startIcon={<DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 32, px: 1.25, borderRadius: '8px',
              color: T.ink700, bgcolor: T.white,
              border: `1px solid ${T.line}`,
              textTransform: 'none', fontSize: 12.5, fontWeight: 500,
              '&:hover': { bgcolor: T.surface, borderColor: T.ink300 },
              '&.Mui-disabled': { color: T.ink400, borderColor: T.lineSoft },
            }}
          >Riemërto</Button>
        </Stack>
      </Stack>

      <DraftBanner count={draftCount} onReview={onReview} onDiscard={onDiscard} />
    </Paper>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * User Overrides Panel (e ruajtur me styling të ri minimal)
 * ═════════════════════════════════════════════════════════════════════════════ */
function UserOverridesPanel({ onSaved }) {
  const [userId, setUserId]       = useState('');
  const [overrides, setOverrides] = useState({ add: [], remove: [] });
  const [newPerm, setNewPerm]     = useState('');
  const [mode, setMode]           = useState('add');
  const [savingOv, setSavingOv]   = useState(false);
  const [toastOv, setToastOv]     = useState({ open: false, msg: '', sev: 'success' });

  // Ngarko override-t kur ndryshon User ID
  useEffect(() => {
    if (!userId.trim()) {
      setOverrides({ add: [], remove: [] });
      return;
    }
    let cancelled = false;
    // Burimi është serveri: paneli tregon çka ZBATOHET, jo çka mbaji ky shfletues.
    Promise.resolve(rolesService.loadUserOverrides(userId.trim())).then((loaded) => {
      if (!cancelled) setOverrides({ add: loaded.add || [], remove: loaded.remove || [] });
    });
    return () => { cancelled = true; };
  }, [userId]);

  const addOverride = () => {
    if (!newPerm.trim()) return;
    setOverrides((prev) => ({
      ...prev,
      [mode]: [...new Set([...prev[mode], newPerm.trim()])],
    }));
    setNewPerm('');
  };

  const removeOverride = (type, perm) => {
    setOverrides((prev) => ({
      ...prev,
      [type]: prev[type].filter((x) => x !== perm),
    }));
  };

  const handleSave = async () => {
    if (!userId.trim()) {
      setToastOv({ open: true, msg: 'Shkruaj ID-në e përdoruesit', sev: 'error' });
      return;
    }
    setSavingOv(true);
    try {
      await rolesService.saveUserOverrides(userId.trim(), overrides);
      onSaved && onSaved();
      setToastOv({
        open: true,
        msg: `Override-t e përdoruesit #${userId} u ruajtën`,
        sev: 'success',
      });
    } catch (e) {
      setToastOv({ open: true, msg: e.message || 'Ruajtja dështoi', sev: 'error' });
    } finally {
      setSavingOv(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ ...cardShell, p: 2.5 }}>
      <Alert severity="info" sx={{
        mb: 2, borderRadius: '10px', fontSize: 12.5,
        bgcolor: T.brandSoft, color: T.brand, border: `1px solid ${alpha(T.brand, 0.18)}`,
        '& .MuiAlert-icon': { color: T.brand },
      }}>
        Shto ose hiq privilegje individuale për një përdorues specifik. Këto override kanë prioritet mbi rolet.
      </Alert>

      <TextField
        label="ID e përdoruesit" size="small" value={userId}
        onChange={(e) => setUserId(e.target.value)}
        placeholder="p.sh. 42"
        InputProps={{
          startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 16 }} /></InputAdornment>,
        }}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13.5 } }}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          label="Çelësi i privilegjit" size="small" value={newPerm}
          onChange={(e) => setNewPerm(e.target.value)}
          placeholder="employees.view.company"
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13.5, fontFamily: 'ui-monospace' } }}
        />
        <Button
          onClick={() => setMode('add')}
          disableElevation
          sx={{
            height: 40, px: 1.5, borderRadius: '10px',
            bgcolor: mode === 'add' ? T.successInk : T.white,
            color:   mode === 'add' ? T.white      : T.successInk,
            border:  `1px solid ${mode === 'add' ? T.successInk : alpha(T.success, 0.35)}`,
            textTransform: 'none', fontSize: 13, fontWeight: 600,
            '&:hover': { bgcolor: mode === 'add' ? T.success : T.successBg },
          }}
        >+ Shto</Button>
        <Button
          onClick={() => setMode('remove')}
          disableElevation
          sx={{
            height: 40, px: 1.5, borderRadius: '10px',
            bgcolor: mode === 'remove' ? T.errorInk : T.white,
            color:   mode === 'remove' ? T.white    : T.errorInk,
            border:  `1px solid ${mode === 'remove' ? T.errorInk : alpha(T.error, 0.35)}`,
            textTransform: 'none', fontSize: 13, fontWeight: 600,
            '&:hover': { bgcolor: mode === 'remove' ? T.error : T.errorBg },
          }}
        >− Hiq</Button>
        <IconButton onClick={addOverride} sx={{
          width: 40, height: 40, borderRadius: '10px',
          bgcolor: T.brand, color: T.white,
          '&:hover': { bgcolor: T.brandHover },
        }}><AddIcon sx={{ fontSize: 18 }} /></IconButton>
      </Stack>

      {overrides.add.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <SectionLabel sx={{ mb: 1 }} count={overrides.add.length}>Të shtuara</SectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {overrides.add.map((perm) => (
              <Chip
                key={perm} label={perm} size="small"
                onDelete={() => removeOverride('add', perm)}
                sx={{
                  fontSize: 11.5, height: 24, borderRadius: '8px',
                  bgcolor: T.successBg, color: T.successInk,
                  border: `1px solid ${alpha(T.success, 0.3)}`,
                  fontFamily: 'ui-monospace, monospace',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {overrides.remove.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <SectionLabel sx={{ mb: 1 }} count={overrides.remove.length}>Të hequra</SectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {overrides.remove.map((perm) => (
              <Chip
                key={perm} label={perm} size="small"
                onDelete={() => removeOverride('remove', perm)}
                sx={{
                  fontSize: 11.5, height: 24, borderRadius: '8px',
                  bgcolor: T.errorBg, color: T.errorInk,
                  border: `1px solid ${alpha(T.error, 0.3)}`,
                  fontFamily: 'ui-monospace, monospace',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {(overrides.add.length > 0 || overrides.remove.length > 0) && (
        <Button
          startIcon={savingOv
            ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
            : <SaveRoundedIcon sx={{ fontSize: 16 }} />}
          disableElevation
          onClick={handleSave}
          disabled={savingOv || !userId.trim()}
          sx={{
            mt: 1, height: 36, px: 1.75, borderRadius: '10px',
            bgcolor: T.brand, color: T.white,
            textTransform: 'none', fontWeight: 600, fontSize: 13,
            '&:hover': { bgcolor: T.brandHover },
            '&.Mui-disabled': { bgcolor: T.surfaceAlt, color: T.ink400 },
          }}
        >Ruaj override-t</Button>
      )}

      <Snackbar
        open={toastOv.open}
        autoHideDuration={3500}
        onClose={() => setToastOv(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toastOv.sev} variant="filled" onClose={() => setToastOv(t => ({ ...t, open: false }))}
          sx={{ fontSize: 13, fontWeight: 500, borderRadius: '10px' }}>
          {toastOv.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * Name Input Dialog (përdoret për Klono / Riemërto / Krijo)
 * ═════════════════════════════════════════════════════════════════════════════ */
function NameInputDialog({ open, payload, onCancel, onSubmit, busy }) {
  const [value, setValue] = useState('');
  useEffect(() => {
    if (open) setValue(payload?.initialValue || '');
  }, [open, payload]);

  if (!payload) return <Dialog open={open} onClose={onCancel} />;

  const trimmed = value.trim();
  const canSubmit = trimmed.length >= 2 && trimmed !== payload.initialValue;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      PaperProps={{ sx: { borderRadius: '14px', maxWidth: 420 } }}
      fullWidth
    >
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: T.ink, pb: 1 }}>
        {payload.title}
      </DialogTitle>
      <DialogContent>
        {payload.hint && (
          <Typography sx={{ fontSize: 13, color: T.ink500, mb: 1.5 }}>
            {payload.hint}
          </Typography>
        )}
        <TextField
          fullWidth size="small" autoFocus
          label={payload.label || 'Emri'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) onSubmit(trimmed);
          }}
          disabled={busy}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px', fontSize: 13.5,
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          disabled={busy}
          sx={{
            height: 34, px: 1.5, borderRadius: '8px',
            color: T.ink700, textTransform: 'none', fontWeight: 500, fontSize: 13,
            '&:hover': { bgcolor: T.surfaceAlt },
          }}
        >Anulo</Button>
        <Button
          onClick={() => onSubmit(trimmed)}
          disabled={!canSubmit || busy}
          disableElevation
          startIcon={busy ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
          sx={{
            height: 34, px: 1.75, borderRadius: '8px',
            bgcolor: T.brand, color: T.white,
            textTransform: 'none', fontWeight: 600, fontSize: 13,
            '&:hover': { bgcolor: T.brandHover },
            '&.Mui-disabled': { bgcolor: T.surfaceAlt, color: T.ink400 },
          }}
        >{payload.confirmLabel || 'Konfirmo'}</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * Audit Log View (tabi Auditimi)
 * ═════════════════════════════════════════════════════════════════════════════ */
function AuditLogView({ entries, onClear }) {
  if (!entries || entries.length === 0) {
    return (
      <Paper elevation={0} sx={{ ...cardShell, p: 4, textAlign: 'center', maxWidth: 520, mx: 'auto' }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: '14px',
          bgcolor: T.brandLight, color: T.brand,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
        }}>
          <HistoryRoundedIcon sx={{ fontSize: 26 }} />
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
          Asnjë veprim akoma
        </Typography>
        <Typography sx={{ fontSize: 13, color: T.ink500, mt: 0.75, lineHeight: 1.55 }}>
          Sapo të ruash ndryshime në role, ato do të shfaqen këtu si historiku i auditimit.
        </Typography>
      </Paper>
    );
  }

  const kindLabels = {
    role_permissions_saved: 'Ruajti privilegjet',
    role_renamed:           'Riemërtoi rolin',
    role_reset:             'Resetoi rolin',
    role_created:           'Krijoi rol të ri',
    role_updated:           'Përditësoi rolin',
    role_deleted:           'Fshiu rolin',
    user_overrides_saved:   'Ruajti override-t e përdoruesit',
  };
  const kindTones = {
    role_permissions_saved: { bg: T.brandLight, fg: T.brand },
    role_renamed:           { bg: T.surfaceAlt, fg: T.ink700 },
    role_reset:             { bg: T.warningBg, fg: T.warningInk },
    role_created:           { bg: T.successBg, fg: T.successInk },
    role_updated:           { bg: T.brandLight, fg: T.brand },
    role_deleted:           { bg: T.errorBg,   fg: T.errorInk },
    user_overrides_saved:   { bg: T.brandSoft, fg: T.brand },
  };

  return (
    <Paper elevation={0} sx={{ ...cardShell, overflow: 'hidden', maxWidth: 880, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{
        px: 2, py: 1.25,
        borderBottom: `1px solid ${T.line}`,
        bgcolor: T.surface,
      }}>
        <SectionLabel count={entries.length}>Historiku</SectionLabel>
        <Button
          size="small"
          onClick={onClear}
          sx={{
            height: 28, px: 1, borderRadius: '6px',
            color: T.ink500, textTransform: 'none', fontSize: 12,
            '&:hover': { bgcolor: T.errorBg, color: T.errorInk },
          }}
        >Pastro historikun</Button>
      </Stack>
      <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
        {entries.map((e, idx) => {
          const tone = kindTones[e.kind] || { bg: T.surfaceAlt, fg: T.ink500 };
          const isLast = idx === entries.length - 1;
          return (
            <Box key={e.id} sx={{
              px: 2, py: 1.5,
              display: 'flex', alignItems: 'flex-start', gap: 1.25,
              borderBottom: isLast ? 0 : `1px solid ${T.lineSoft}`,
            }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '8px',
                bgcolor: tone.bg, color: tone.fg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <HistoryRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.875} flexWrap="wrap">
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: T.ink, lineHeight: 1.4 }}>
                    {e.actor}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: T.ink500 }}>
                    {kindLabels[e.kind] || e.kind}
                  </Typography>
                  {e.roleLabel && (
                    <Box sx={{
                      height: 18, px: 0.75, borderRadius: '6px',
                      bgcolor: T.brandSoft, color: T.brand,
                      fontSize: 11, fontWeight: 600,
                      display: 'inline-flex', alignItems: 'center',
                    }}>{e.roleLabel}</Box>
                  )}
                  {e.userId && (
                    <Box sx={{
                      height: 18, px: 0.75, borderRadius: '6px',
                      bgcolor: T.surfaceAlt, color: T.ink700,
                      fontSize: 11, fontWeight: 600,
                      display: 'inline-flex', alignItems: 'center',
                      fontFamily: 'ui-monospace, monospace',
                    }}>user #{e.userId}</Box>
                  )}
                </Stack>
                {(e.added?.length > 0 || e.removed?.length > 0) && (
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                    {e.added?.length > 0 && (
                      <Typography sx={{ fontSize: 12, color: T.successInk }}>
                        + {e.added.length} të shtuara
                      </Typography>
                    )}
                    {e.removed?.length > 0 && (
                      <Typography sx={{ fontSize: 12, color: T.errorInk }}>
                        − {e.removed.length} të hequra
                      </Typography>
                    )}
                  </Stack>
                )}
                {(e.addCount != null || e.removeCount != null) && (
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                    {e.addCount > 0 && (
                      <Typography sx={{ fontSize: 12, color: T.successInk }}>
                        + {e.addCount} të shtuara
                      </Typography>
                    )}
                    {e.removeCount > 0 && (
                      <Typography sx={{ fontSize: 12, color: T.errorInk }}>
                        − {e.removeCount} të hequra
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>
              <Typography sx={{
                fontSize: 11.5, color: T.ink400,
                fontFamily: 'ui-monospace, monospace',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {new Date(e.at).toLocaleString('sq-AL', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
 * Main Page
 * ═════════════════════════════════════════════════════════════════════════════ */
export default function RolesPermissionsPage({ embedded = false }) {
  const { can, hasAnyPermission } = usePermissions();
  const canManage = can('roles_permissions.manage');
  const canView   = hasAnyPermission(['roles_permissions.view', 'roles_permissions.manage']);

  const [tab, setTab]               = useState(0);
  const [selectedRole, setSelectedRole] = useState(ROLES.HR_MANAGER);
  const [roleSearch, setRoleSearch] = useState('');
  const [filterText, setFilterText] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [draftByRole, setDraftByRole] = useState({});
  /**
   * Pamja e thjeshtë është e parazgjedhur; zgjedhja e përdoruesit mbahet.
   *
   * Kush hyn këtu një herë në gjashtë muaj s'duhet të përballet me njëmbëdhjetë
   * veprime × pesë shtrirje për modul. Kush i cakton rolet çdo javë e kthen
   * çelësin një herë dhe e gjen ashtu herës tjetër.
   */
  const [advancedMode, setAdvancedMode] = useState(() => {
    try { return localStorage.getItem('nexuma.perms.advanced') === '1'; } catch { return false; }
  });
  const setAdvanced = (on) => {
    setAdvancedMode(on);
    try { localStorage.setItem('nexuma.perms.advanced', on ? '1' : '0'); } catch { /* private mode */ }
  };
  const [expandedSet, setExpandedSet] = useState(() => new Set([Object.keys(MODULE_PERMISSIONS_MAP)[0]]));
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirm, setConfirm]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [toast, setToast]           = useState({ open: false, msg: '', sev: 'success' });

  // Persistencë
  const [overrides, setOverrides]   = useState(() => rolesService.loadRoleOverrides());
  const [customRoles, setCustomRoles] = useState([]);
  const [auditLog, setAuditLog]     = useState(() => rolesService.getAuditLog());

  // Dialog state (Klono / Riemërto / Krijo)
  const [nameDialog, setNameDialog] = useState(null);
  // shape: { kind: 'clone'|'rename'|'create', title, label, value, hint }

  const moduleRefs = useRef({});

  // Ngarko custom roles + override-t e roleve bazë nga backend në mount.
  useEffect(() => {
    let mounted = true;
    rolesService.listCustomRoles().then(list => {
      if (mounted) setCustomRoles(list);
    });
    rolesService.listRoleOverrides().then(map => {
      if (mounted) setOverrides(map);
    });
    return () => { mounted = false; };
  }, []);

  const refreshOverrides = useCallback(async () => {
    const map = await rolesService.listRoleOverrides();
    setOverrides(map);
  }, []);
  const refreshAudit = useCallback(() => {
    setAuditLog(rolesService.getAuditLog());
  }, []);
  const refreshCustomRoles = useCallback(async () => {
    const list = await rolesService.listCustomRoles();
    setCustomRoles(list);
  }, []);

  // ── derived ────────────────────────────────────────────────────────────────
  // Bashkimi i roleve sistemike + custom roles në një listë të vetme.
  const allRoles = useMemo(() => {
    const list = [];
    // Built-in
    Object.values(ROLES_CONFIG).forEach(r => {
      const ov = overrides[r.key];
      list.push({
        ...r,
        label: ov?.label || r.label,
        permissions: ov?.permissions || r.permissions,
        _kind: 'builtin',
      });
    });
    // Custom (nga backend)
    customRoles.forEach(cr => {
      let perms = [];
      try {
        perms = typeof cr.permissions === 'string'
          ? JSON.parse(cr.permissions)
          : (Array.isArray(cr.permissions) ? cr.permissions : []);
      } catch { perms = []; }
      list.push({
        key: `custom:${cr.id}`,
        label: cr.name,
        description: cr.description || '',
        priority: 25,
        color: cr.color || T.brand,
        bgColor: T.brandLight,
        permissions: perms,
        _kind: 'custom',
        _customId: cr.id,
      });
    });
    return list;
  }, [overrides, customRoles]);

  const rolesByKey = useMemo(() => {
    const m = {};
    allRoles.forEach(r => { m[r.key] = r; });
    return m;
  }, [allRoles]);

  // Nëse roli i zgjedhur nuk ekziston më (p.sh. u fshi), kthehu te i pari
  useEffect(() => {
    if (allRoles.length > 0 && !rolesByKey[selectedRole]) {
      setSelectedRole(allRoles[0].key);
    }
  }, [allRoles, rolesByKey, selectedRole]);

  const role = rolesByKey[selectedRole];
  const isCustom = role?._kind === 'custom';
  const readOnly = !canManage || isSystemRole(selectedRole);
  const baseSet = useMemo(() => new Set(role?.permissions || []), [role]);
  const draft   = draftByRole[selectedRole] || EMPTY_DRAFT;
  const draftCount = draft.add.size + draft.remove.size;
  const effective  = useMemo(() => effectiveSetFor(baseSet, draft), [baseSet, draft]);

  // module-level summary
  const moduleSummary = useMemo(() => {
    const out = {};
    Object.keys(MODULE_PERMISSIONS_MAP).forEach(moduleKey => {
      const perms = MODULE_PERMISSIONS_MAP[moduleKey];
      const total = perms.length;
      let active = 0;
      perms.forEach(({ action, scope }) => {
        if (effective.has(cellKey(moduleKey, { action, scope }))) active += 1;
      });
      out[moduleKey] = { total, active };
    });
    return out;
  }, [effective]);

  const filteredModules = useMemo(() => {
    return Object.keys(MODULE_PERMISSIONS_MAP).filter(moduleKey => {
      const label = moduleLabel(moduleKey).toLowerCase();
      if (filterText && !label.includes(filterText.toLowerCase())) return false;
      if (showOnlyActive && moduleSummary[moduleKey].active === 0) return false;
      return true;
    });
  }, [filterText, showOnlyActive, moduleSummary]);

  // roles list grouped (built-in + custom)
  const rolesGrouped = useMemo(() => {
    const groups = { sistemi: [], funksionale: [], mjedis: [], custom: [] };
    allRoles
      .slice()
      .sort((a, b) => b.priority - a.priority)
      .filter(r => !roleSearch || r.label.toLowerCase().includes(roleSearch.toLowerCase()))
      .forEach(r => {
        if (r._kind === 'custom') groups.custom.push(r);
        else groups[roleGroup(r)].push(r);
      });
    return groups;
  }, [allRoles, roleSearch]);

  const draftCountFor = (rk) => {
    const d = draftByRole[rk] || EMPTY_DRAFT;
    return d.add.size + d.remove.size;
  };

  // ── actions ────────────────────────────────────────────────────────────────
  function applyToggle(permKey, turnOn) {
    setDraftByRole(prev => {
      const cur = prev[selectedRole] || EMPTY_DRAFT;
      const next = computeNextDraft(permKey, baseSet, cur, turnOn);
      const empty = next.add.size === 0 && next.remove.size === 0;
      const out = { ...prev };
      if (empty) delete out[selectedRole];
      else       out[selectedRole] = next;
      return out;
    });
  }

  function handleToggle(permKey, { action, scope }, turnOn) {
    if (readOnly) return;
    // Critical confirm only when GRANTING a critical permission
    if (turnOn && isCriticalToggle({ action, scope })) {
      setConfirm({
        permKey, turnOn,
        roleLabel:   role?.label || '',
        actionLabel: ACTION_DEFS[action]?.label || action,
        scopeLabel:  scope ? SCOPE_LABELS[scope] : null,
        moduleLabel: moduleLabel(permKey.split('.')[0]),
      });
      return;
    }
    applyToggle(permKey, turnOn);
  }

  /**
   * Një kuti e pamjes së thjeshtë prek disa leje njëherësh.
   *
   * Ndezja jep një shtrirje të vetme për veprim (shih `SIMPLE_GRANT_SCOPES`);
   * fikja heq çdo shtrirje të atij veprimi — përndryshe një leje e mbetur te
   * «Ekipi» do ta linte kutinë të fikur ndërsa e drejta vazhdon të ekzistojë.
   */
  function toggleSimple(moduleKey, col, turnOn) {
    if (readOnly) return;
    if (turnOn && col.critical) {
      setConfirm({
        simple: { moduleKey, col },
        roleLabel:   role?.label || '',
        actionLabel: col.label,
        scopeLabel:  null,
        moduleLabel: moduleLabel(moduleKey),
      });
      return;
    }
    applySimple(moduleKey, col, turnOn);
  }

  function applySimple(moduleKey, col, turnOn) {
    setDraftByRole(prev => {
      const cur = prev[selectedRole] || EMPTY_DRAFT;
      let next = { add: new Set(cur.add), remove: new Set(cur.remove) };
      const keys = turnOn ? simpleGrantKeys(moduleKey, col.actions) : simpleKeys(moduleKey, col.actions);
      keys.forEach(k => { next = computeNextDraft(k, baseSet, next, turnOn); });
      const empty = next.add.size === 0 && next.remove.size === 0;
      const out = { ...prev };
      if (empty) delete out[selectedRole];
      else       out[selectedRole] = next;
      return out;
    });
  }

  function confirmCritical() {
    if (!confirm) return;
    if (confirm.simple) applySimple(confirm.simple.moduleKey, confirm.simple.col, true);
    else                applyToggle(confirm.permKey, confirm.turnOn);
    setConfirm(null);
  }

  function cancelAll() {
    setDraftByRole(prev => {
      const out = { ...prev };
      delete out[selectedRole];
      return out;
    });
    setToast({ open: true, msg: 'Ndryshimet u anuluan', sev: 'info' });
  }

  function resetModule(moduleKey) {
    setDraftByRole(prev => {
      const cur = prev[selectedRole] || EMPTY_DRAFT;
      const next = { add: new Set(cur.add), remove: new Set(cur.remove) };
      const perms = MODULE_PERMISSIONS_MAP[moduleKey] || [];
      perms.forEach(({ action, scope }) => {
        const k = cellKey(moduleKey, { action, scope });
        next.add.delete(k);
        next.remove.delete(k);
      });
      const empty = next.add.size === 0 && next.remove.size === 0;
      const out = { ...prev };
      if (empty) delete out[selectedRole];
      else       out[selectedRole] = next;
      return out;
    });
  }

  function fillModule(moduleKey) {
    setDraftByRole(prev => {
      const cur = prev[selectedRole] || EMPTY_DRAFT;
      const next = { add: new Set(cur.add), remove: new Set(cur.remove) };
      (MODULE_PERMISSIONS_MAP[moduleKey] || []).forEach(({ action, scope }) => {
        const k = cellKey(moduleKey, { action, scope });
        // Skip critical when bulk-filling for safety
        if (isCriticalToggle({ action, scope })) return;
        if (baseSet.has(k)) { next.remove.delete(k); }
        else                { next.add.add(k); next.remove.delete(k); }
      });
      return { ...prev, [selectedRole]: next };
    });
  }

  async function persistDraft() {
    if (!role) return;
    setSaving(true);
    try {
      const effectiveList = Array.from(effective);
      const diff = {
        added:   Array.from(draft.add),
        removed: Array.from(draft.remove),
      };

      if (isCustom) {
        // Custom role → ruaj në backend përmes /api/customRoles
        await rolesService.updateCustomRole(role._customId, {
          name:        role.label,
          description: role.description,
          color:       role.color,
          permissions: effectiveList,
        });
        await refreshCustomRoles();
      } else {
        // Built-in role → ruaj override në localStorage
        await rolesService.saveRolePermissions(selectedRole, effectiveList, {
          roleLabel: role.label,
          diff,
        });
        refreshOverrides();
      }

      refreshAudit();
      setDraftByRole(prev => {
        const out = { ...prev };
        delete out[selectedRole];
        return out;
      });
      setReviewOpen(false);
      setToast({
        open: true,
        msg: `Privilegjet e rolit ${role.label} u ruajtën (${diff.added.length} të shtuara, ${diff.removed.length} të hequra)`,
        sev: 'success',
      });
    } catch (err) {
      setToast({ open: true, msg: err.message || 'Ruajtja dështoi', sev: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function expandAll() { setExpandedSet(new Set(Object.keys(MODULE_PERMISSIONS_MAP))); }
  function collapseAll() { setExpandedSet(new Set()); }

  // ── Klono / Riemërto / Krijo handlers ─────────────────────────────────────
  function openClone() {
    if (!role) return;
    setNameDialog({
      kind: 'clone',
      title: `Klono rolin "${role.label}"`,
      hint: 'Do të krijohet një rol i ri me të njëjtat privilegje. Mund ta modifikosh menjëherë pas krijimit.',
      label: 'Emri i rolit të ri',
      initialValue: `${role.label} (kopje)`,
      confirmLabel: 'Klono rolin',
    });
  }
  function openRename() {
    if (!role) return;
    setNameDialog({
      kind: 'rename',
      title: `Riemërto rolin "${role.label}"`,
      hint: isCustom
        ? 'Emri i ri ruhet menjëherë në bazën e të dhënave.'
        : 'Emri i ri ruhet lokalisht si override mbi konfigurimin sistemik.',
      label: 'Emri i ri',
      initialValue: role.label,
      confirmLabel: 'Riemërto',
    });
  }
  function openCreate() {
    setNameDialog({
      kind: 'create',
      title: 'Krijo rol të ri',
      hint: 'Roli do të krijohet bosh; mund t\'i japësh privilegje menjëherë pas krijimit.',
      label: 'Emri i rolit',
      initialValue: '',
      confirmLabel: 'Krijo rolin',
    });
  }
  async function submitNameDialog(newName) {
    if (!nameDialog) return;
    const kind = nameDialog.kind;
    setSaving(true);
    try {
      if (kind === 'clone') {
        const created = await rolesService.createCustomRole({
          name: newName,
          description: `Klonuar nga ${role.label}`,
          color: T.brand,
          permissions: Array.from(effective),
        });
        await refreshCustomRoles();
        refreshAudit();
        setSelectedRole(`custom:${created.id}`);
        setToast({ open: true, msg: `Roli "${newName}" u klonua`, sev: 'success' });
      } else if (kind === 'create') {
        const created = await rolesService.createCustomRole({
          name: newName,
          description: '',
          color: T.brand,
          permissions: [],
        });
        await refreshCustomRoles();
        refreshAudit();
        setSelectedRole(`custom:${created.id}`);
        setToast({ open: true, msg: `Roli "${newName}" u krijua`, sev: 'success' });
      } else if (kind === 'rename') {
        if (isCustom) {
          await rolesService.updateCustomRole(role._customId, {
            name: newName,
            description: role.description,
            color: role.color,
            permissions: role.permissions,
          });
          await refreshCustomRoles();
        } else {
          await rolesService.renameRole(selectedRole, newName);
          await refreshOverrides();
        }
        refreshAudit();
        setToast({ open: true, msg: 'Roli u riemërtua', sev: 'success' });
      }
      setNameDialog(null);
    } catch (err) {
      setToast({ open: true, msg: err.message || 'Veprimi dështoi', sev: 'error' });
    } finally {
      setSaving(false);
    }
  }
  function jumpToModule(moduleKey) {
    setReviewOpen(false);
    setExpandedSet(prev => new Set([...prev, moduleKey]));
    setTimeout(() => {
      const el = moduleRefs.current[moduleKey];
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  // Build review lists
  const reviewLists = useMemo(() => {
    const buildLabel = (permKey) => {
      const parts = permKey.split('.');
      const moduleKey = parts[0];
      const action    = parts[1];
      const scope     = parts[2];
      const actLabel  = ACTION_DEFS[action]?.label || action;
      const scopeLabel = scope ? SCOPE_LABELS[scope] : null;
      return {
        moduleKey, permKey,
        label: scopeLabel ? `${actLabel} · ${scopeLabel}` : actLabel,
      };
    };
    return {
      add:    Array.from(draft.add).map(buildLabel),
      remove: Array.from(draft.remove).map(buildLabel),
    };
  }, [draft]);

  if (!canView) return <AccessDenied requiredPermission="roles_permissions.view" />;

  return (
    <Box sx={{ bgcolor: T.surface, minHeight: '100%' }}>
      {/* ═══════════════════════ HEADER STICKY ═══════════════════════ */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 50,
        bgcolor: T.white,
        borderBottom: `1px solid ${T.line}`,
        px: { xs: 2, md: 3 },
        py: 1,
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          {/* Title (fshihet kur është embedded brenda një faqeje tjetër) */}
          {!embedded ? (
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '10px',
                bgcolor: T.brandLight, color: T.brand,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <SecurityRoundedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>
                Rolet & Privilegjet
              </Typography>
            </Stack>
          ) : <Box />}

          {/* Tabs (center on lg) */}
          <Tabs
            value={tab} onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': { bgcolor: T.brand, height: 2, borderRadius: '2px 2px 0 0' },
              '& .MuiTab-root': {
                minHeight: 40, textTransform: 'none',
                fontSize: 13, fontWeight: 500, color: T.ink500,
                px: 1.75,
                '&:hover': { color: T.ink },
                '&.Mui-selected': { color: T.ink, fontWeight: 600 },
              },
            }}
          >
            <Tab label="Rolet" />
            <Tab label="Përdoruesit" disabled={!canManage} />
            <Tab label="Auditimi" />
          </Tabs>

          {/* Status + Save bar */}
          <Stack direction="row" alignItems="center" spacing={1}>
            {tab === 0 && (
              <>
                <StatusPill count={effective.size} dirty={draftCount > 0} />
                <Button
                  size="small"
                  onClick={cancelAll}
                  disabled={draftCount === 0 || saving}
                  sx={{
                    height: 34, px: 1.5, borderRadius: '8px',
                    color: T.ink700, textTransform: 'none', fontWeight: 500, fontSize: 13,
                    border: `1px solid ${T.line}`,
                    '&:hover': { bgcolor: T.surface, borderColor: T.ink300 },
                    '&.Mui-disabled': { color: T.ink300, borderColor: T.lineSoft },
                  }}
                >Anulo</Button>
                <Button
                  size="small"
                  onClick={() => setReviewOpen(true)}
                  disabled={draftCount === 0 || saving}
                  disableElevation
                  startIcon={saving
                    ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                    : <SaveRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    height: 34, px: 1.75, borderRadius: '8px',
                    bgcolor: T.brand, color: T.white,
                    textTransform: 'none', fontWeight: 600, fontSize: 13,
                    '&:hover': { bgcolor: T.brandHover },
                    '&.Mui-disabled': { bgcolor: T.surfaceAlt, color: T.ink400 },
                  }}
                >Ruaj{draftCount > 0 ? ` (${draftCount})` : ''}</Button>
              </>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* ═══════════════════════ TAB CONTENT ═══════════════════════ */}
      {tab === 0 && (
        <Box sx={{
          p: { xs: 1.5, md: 2 },
          display: { xs: 'block', lg: 'grid' },
          gridTemplateColumns: { lg: '300px 1fr' },
          gap: { lg: 2 },
          alignItems: 'start',
        }}>
          {/* ───── LEFT — Roles ───── */}
          <Box sx={{
            position: { lg: 'sticky' }, top: { lg: 72 },
            maxHeight: { lg: 'calc(100vh - 88px)' },
            display: 'flex', flexDirection: 'column', gap: 1.25,
            minHeight: 0, mb: { xs: 2, lg: 0 },
          }}>
            {/* Search */}
            <Paper elevation={0} sx={{ ...cardShell, p: 1, flexShrink: 0 }}>
              <TextField
                size="small" fullWidth
                placeholder="Kërko rol…"
                value={roleSearch}
                onChange={(e) => setRoleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 17, color: T.ink400 }} />
                    </InputAdornment>
                  ),
                  endAdornment: roleSearch ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setRoleSearch('')} sx={{ p: 0.25 }}>
                        <CloseRoundedIcon sx={{ fontSize: 14, color: T.ink400 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36, borderRadius: '8px', bgcolor: T.surface, fontSize: 13,
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover': { bgcolor: T.surfaceAlt },
                    '&:hover fieldset': { borderColor: 'transparent' },
                    '&.Mui-focused': { bgcolor: T.white, boxShadow: `0 0 0 3px ${alpha(T.brand, 0.15)}` },
                    '&.Mui-focused fieldset': { borderColor: T.brand },
                  },
                }}
              />
            </Paper>

            {/* List */}
            <Paper elevation={0} sx={{
              ...cardShell, overflow: 'hidden',
              flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
            }}>
              <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, py: 0.75 }}>
                {['sistemi', 'funksionale', 'mjedis'].map(groupKey => {
                  const list = rolesGrouped[groupKey] || [];
                  if (list.length === 0) return null;
                  return (
                    <Box key={groupKey} sx={{ mb: 0.5 }}>
                      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
                        <SectionLabel>{GROUP_LABELS[groupKey]}</SectionLabel>
                      </Box>
                      {list.map(r => (
                        <RoleCard
                          key={r.key}
                          role={r}
                          selected={selectedRole === r.key}
                          readOnly={isSystemRole(r.key)}
                          draftCount={draftCountFor(r.key)}
                          onClick={() => setSelectedRole(r.key)}
                        />
                      ))}
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{
                borderTop: `1px solid ${T.line}`,
                p: 1, bgcolor: T.surface,
              }}>
                <Button
                  fullWidth
                  onClick={openCreate}
                  disabled={!canManage}
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    height: 34, borderRadius: '8px',
                    color: T.brand, textTransform: 'none', fontSize: 12.5, fontWeight: 600,
                    border: `1px dashed ${alpha(T.brand, 0.4)}`,
                    bgcolor: T.white,
                    '&:hover': { bgcolor: T.brandSoft, borderColor: T.brand },
                    '&.Mui-disabled': { color: T.ink400, borderColor: T.line },
                  }}
                >Krijo rol të ri</Button>
              </Box>
            </Paper>
          </Box>

          {/* ───── RIGHT — Workspace ───── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
            {role && (
              <RoleSummaryCard
                role={role}
                draftCount={draftCount}
                activeCount={effective.size}
                readOnly={readOnly}
                onReview={() => setReviewOpen(true)}
                onDiscard={cancelAll}
                onClone={openClone}
                onRename={openRename}
              />
            )}

            {/* Controls bar */}
            <Paper elevation={0} sx={{
              ...cardShell, p: 1.25,
              position: 'sticky', top: 64, zIndex: 5,
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
            }}>
              <TextField
                size="small"
                placeholder="Filtro modulin…"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: T.ink400 }} />
                    </InputAdornment>
                  ),
                  endAdornment: filterText ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setFilterText('')} sx={{ p: 0.25 }}>
                        <CloseRoundedIcon sx={{ fontSize: 14, color: T.ink400 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  minWidth: 220,
                  '& .MuiOutlinedInput-root': {
                    height: 36, borderRadius: '8px', bgcolor: T.surface, fontSize: 13,
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover': { bgcolor: T.surfaceAlt },
                    '&:hover fieldset': { borderColor: 'transparent' },
                    '&.Mui-focused': { bgcolor: T.white, boxShadow: `0 0 0 3px ${alpha(T.brand, 0.15)}` },
                    '&.Mui-focused fieldset': { borderColor: T.brand },
                  },
                }}
              />

              <FormControlLabel
                control={
                  <Switch
                    size="small" checked={showOnlyActive}
                    onChange={(e) => setShowOnlyActive(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: T.brand },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: T.brand },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: 12.5, color: T.ink700 }}>Vetëm aktive</Typography>}
              />

              <FormControlLabel
                control={
                  <Switch
                    size="small" checked={advancedMode}
                    onChange={(e) => setAdvanced(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: T.brand },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: T.brand },
                    }}
                  />
                }
                label={
                  <Tooltip title="Shto shtrirjet: Veta, Ekipi, Departamenti, Kompania, Të gjitha" arrow placement="top">
                    <Typography sx={{ fontSize: 12.5, color: T.ink700 }}>Të avancuara</Typography>
                  </Tooltip>
                }
              />

              <Box sx={{ flex: 1 }} />

              {/* Zgjero/Mbyll kanë kuptim vetëm te akordeonet e pamjes së avancuar. */}
              {advancedMode && (
                <>
              <Tooltip title="Zgjero të gjitha" arrow placement="top">
                <IconButton onClick={expandAll} size="small" sx={{
                  width: 32, height: 32, borderRadius: '8px', color: T.ink500,
                  '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
                }}>
                  <UnfoldMoreRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Mbyll të gjitha" arrow placement="top">
                <IconButton onClick={collapseAll} size="small" sx={{
                  width: 32, height: 32, borderRadius: '8px', color: T.ink500,
                  '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
                }}>
                  <UnfoldLessRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
                </>
              )}
            </Paper>

            {/* Matrix */}
            <Paper elevation={0} sx={{ ...cardShell, overflow: 'hidden' }}>
              {filteredModules.length === 0 ? (
                <Box sx={{ p: 5, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink }}>
                    Asnjë modul nuk përputhet
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: T.ink500, mt: 0.5 }}>
                    Provo një term tjetër ose çaktivizo "Vetëm aktive".
                  </Typography>
                </Box>
              ) : !advancedMode ? (
                <SimpleMatrix
                  modules={filteredModules}
                  effective={effective}
                  disabled={readOnly}
                  onToggle={toggleSimple}
                  // Kalimi te pamja e avancuar me modulin tashmë të hapur: pa
                  // këtë, kolona «Të tjera» do të tregonte një të drejtë pa
                  // dhënë asnjë rrugë për ta prekur.
                  onOpenAdvanced={(moduleKey) => {
                    setAdvanced(true);
                    setExpandedSet(prev => new Set(prev).add(moduleKey));
                  }}
                />
              ) : (
                filteredModules.map(moduleKey => {
                  const expanded = expandedSet.has(moduleKey);
                  const actions  = buildModuleActions(moduleKey);
                  const summary  = moduleSummary[moduleKey];
                  return (
                    <Box
                      key={moduleKey}
                      ref={(el) => { moduleRefs.current[moduleKey] = el; }}
                    >
                      <ModuleAccordion
                        moduleKey={moduleKey}
                        expanded={expanded}
                        activeCount={summary.active}
                        totalCount={summary.total}
                        disabled={readOnly}
                        onToggleExpanded={() => {
                          setExpandedSet(prev => {
                            const next = new Set(prev);
                            if (next.has(moduleKey)) next.delete(moduleKey);
                            else                     next.add(moduleKey);
                            return next;
                          });
                        }}
                        onReset={() => resetModule(moduleKey)}
                        onFillAll={() => fillModule(moduleKey)}
                      >
                        {actions.map(({ action, scopes, hasNoScope }, idx) => (
                          <ActionRow
                            key={action}
                            moduleKey={moduleKey}
                            action={action}
                            scopes={scopes}
                            hasNoScope={hasNoScope}
                            effective={effective}
                            disabled={readOnly}
                            even={idx % 2 === 1}
                            isLast={idx === actions.length - 1}
                            onToggle={handleToggle}
                          />
                        ))}
                      </ModuleAccordion>
                    </Box>
                  );
                })
              )}
            </Paper>

            {readOnly && (
              <Alert
                severity="info"
                icon={<LockRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  borderRadius: '10px', fontSize: 12.5,
                  bgcolor: T.surfaceAlt, color: T.ink700, border: `1px solid ${T.line}`,
                  '& .MuiAlert-icon': { color: T.ink500 },
                }}
              >
                Ky rol është sistemik (vetëm lexim). Privilegjet nuk mund të modifikohen.
              </Alert>
            )}
          </Box>
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720, mx: 'auto' }}>
          <UserOverridesPanel onSaved={refreshAudit} />
        </Box>
      )}

      {tab === 2 && (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <AuditLogView
            entries={auditLog}
            onClear={() => {
              rolesService.clearAuditLog();
              refreshAudit();
              setToast({ open: true, msg: 'Historiku u pastrua', sev: 'info' });
            }}
          />
        </Box>
      )}

      {/* Review drawer */}
      <ReviewDrawer
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        roleLabel={role?.label || ''}
        addList={reviewLists.add}
        removeList={reviewLists.remove}
        onJumpTo={jumpToModule}
        onDiscardAll={cancelAll}
        onSave={persistDraft}
        saving={saving}
      />

      {/* Critical confirm */}
      <CriticalConfirm
        open={!!confirm}
        payload={confirm}
        onCancel={() => setConfirm(null)}
        onConfirm={confirmCritical}
      />

      {/* Name input dialog (Klono / Riemërto / Krijo) */}
      <NameInputDialog
        open={!!nameDialog}
        payload={nameDialog}
        busy={saving}
        onCancel={() => setNameDialog(null)}
        onSubmit={submitNameDialog}
      />

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.sev} variant="filled"
          onClose={() => setToast(t => ({ ...t, open: false }))}
          sx={{
            fontSize: 13, fontWeight: 500, borderRadius: '10px',
            boxShadow: '0 10px 24px rgba(15,23,42,.12)',
          }}
        >{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
